import * as React from 'react';

import { enumFacilityGroupSortOption } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Select, type SelectProps } from '@rematter/ui-web';

import { useFacilityGroupsQuery } from '~/modules/facilities/hooks/useFacilityGroupsQuery';

export type FacilityGroupSelectProps = Omit<
  SelectProps<string>,
  'options' | 'selectedKey' | 'onSelectionChange' | 'isLoading'
> & {
  value?: string;
  onChange?: (value: string | number | null | undefined) => void;
};

export const FacilityGroupSelect = ({
  value,
  onChange,
  ...props
}: FacilityGroupSelectProps) => {
  const t = useTranslations();
  const hasSetInitialValue = React.useRef(false);

  const { data, isLoading } = useFacilityGroupsQuery({
    sorting: [enumFacilityGroupSortOption.NameAsc],
    offset: 0,
    limit: 100, // Increased limit since we're doing client-side filtering
  });

  const options = React.useMemo(
    () =>
      data?.facilityGroups?.map((facilityGroup) => ({
        key: facilityGroup.id,
        label: facilityGroup.name,
      })) ?? [],
    [data?.facilityGroups],
  );

  React.useEffect(() => {
    if (!value && options.length > 0 && !hasSetInitialValue.current) {
      onChange?.(options[0].key);
      hasSetInitialValue.current = true;
    }
  }, [value, onChange, options]);

  return (
    <Select
      {...props}
      options={options}
      value={value}
      onChange={onChange}
      isLoading={isLoading}
      placeholder={t.servicesView.form.placeholders.facilityGroup}
      isRequired
      className="@FacilityGroupSelect"
    />
  );
};
